import React from 'react';
import { Compass, Layers, MapPin, ArrowRight, PhoneCall, Check } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

export const EligibilitySection: React.FC = () => {
  const criteria = [
    {
      icon: Compass,
      num: '01',
      title: '일조량 및 음영',
      badge: '남향 & 음영 최소',
      desc: '하루 일조량이 풍부하고, 주변 고층 건물이나 산으로 인한 일사 장애가 없는 부지',
    },
    {
      icon: Layers,
      num: '02',
      title: '소유 권리',
      badge: '건물주 / 토지주',
      desc: '본인 또는 법인 소유의 건물 및 토지 (임차인인 경우 소유주 동의 후 진행 가능)',
    },
    {
      icon: MapPin,
      num: '03',
      title: '대상 지역',
      badge: '전국 100% 지원',
      desc: '전국 어느 지역이든 신속 현장 방문 조사 및 전담 엔지니어 우선 배정',
    },
  ];

  const steps = [
    {
      step: '01',
      title: '무료 현장 조사 & 진단',
      desc: '전문 엔지니어가 직접 방문하여 지붕 노후도, 방수 상태, 일조량을 정밀 측정합니다.',
    },
    {
      step: '02',
      title: '수익 분석 & 수리비 산정',
      desc: '지붕 수리 지원금 규모와 20년 연 임대료/발전 수익률 상세 견적표를 제공합니다.',
    },
    {
      step: '03',
      title: '1등급 시공 & 지붕 수리',
      desc: '검증된 1등급 전문 시공사가 지붕 방수 수리 및 안전한 태양광 인허가 시공을 완료합니다.',
    },
    {
      step: '04',
      title: '임대료 정산 / 발전 시작',
      desc: '시공 완료 즉시 약정된 임대료(또는 일시불 선지급)를 정산받고 수익을 창출합니다.',
    },
  ];

  const scrollToConsultation = () => {
    const elem = document.getElementById('consultation');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="eligibility" className="py-24 sm:py-32 bg-[#0B0F19] text-white relative overflow-hidden border-t border-b border-slate-800/80">
      
      {/* Ambient background light glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-sky-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-blue-600/10 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-5 leading-tight keep-all">
              설치 조건 및 자격 안내 <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-sky-300 to-blue-400">
                우리 건물도 설치가 가능할까요?
              </span>
            </h2>
            
            <p className="text-slate-400 text-sm sm:text-base font-medium leading-relaxed keep-all">
              건물 및 부지 소유주라면 누구나 지원 가능. 복잡한 서류 절차 없이 빠르게 진단해 드립니다.
            </p>
          </div>
        </ScrollReveal>

        {/* 3 Core Qualification Criteria Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mb-12 sm:mb-16">
          {criteria.map((item, idx) => {
            const IconComp = item.icon;
            return (
              <ScrollReveal key={idx} direction="up" delay={idx * 0.1}>
                <div className="h-full bg-slate-900/60 backdrop-blur-xl rounded-2xl p-5 sm:p-8 border border-slate-800 hover:border-sky-500/50 transition-all duration-300 group flex flex-col justify-between hover:-translate-y-1 shadow-lg">
                  <div>
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 flex items-center justify-center group-hover:bg-sky-500 group-hover:text-white transition-colors">
                        <IconComp className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-xs font-mono font-bold text-slate-500">
                        {item.num}
                      </span>
                    </div>

                    <span className="text-[10px] sm:text-xs font-bold text-sky-400 bg-sky-500/10 border border-sky-500/20 px-2.5 py-0.5 sm:py-1 rounded-md inline-block mb-2 sm:mb-3">
                      {item.badge}
                    </span>

                    <h3 className="text-lg sm:text-xl font-black text-white mb-1.5 sm:mb-2 tracking-tight">
                      {item.title}
                    </h3>

                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed font-normal keep-all">
                      {item.desc}
                    </p>
                  </div>

                  <div className="mt-6 sm:mt-8 pt-3.5 sm:pt-4 border-t border-slate-800/60 flex items-center gap-2 text-xs font-bold text-slate-400 group-hover:text-sky-400 transition-colors">
                    <Check className="w-4 h-4 text-sky-400" />
                    <span>지원 자격 충족 여부 확인 가능</span>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Nationwide Banner */}
        <ScrollReveal direction="up">
          <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-sky-950/40 to-slate-900 border border-slate-800 p-5 sm:p-8 mb-14 sm:mb-20 flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-6 shadow-xl">
            <div className="flex items-center gap-3.5 sm:gap-4">
              <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <div>
                <h4 className="text-base sm:text-xl font-black text-white">
                  전국 어디서나 <span className="text-sky-400">(전국 전 지역 지원)</span>
                </h4>
                <p className="text-xs sm:text-sm text-slate-400 font-normal mt-0.5">
                  지역 제한 없이 전국 어디든 전문 진단 엔지니어가 신속하게 출장 배정됩니다.
                </p>
              </div>
            </div>

            <a
              href="tel:01027671449"
              className="w-full sm:w-auto justify-center shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 font-bold text-xs sm:text-sm transition-colors whitespace-nowrap"
            >
              <PhoneCall className="w-4 h-4 text-sky-400" />
              <span>010-2767-1449 빠른 상담</span>
            </a>
          </div>
        </ScrollReveal>

        {/* 4-Step Process Section ("진행 절차") */}
        <div className="space-y-12">
          <ScrollReveal direction="up">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl sm:text-4xl font-black text-white tracking-tight mb-3">
                진행 절차
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 font-normal keep-all">
                복잡한 인허가 및 시공, 지붕 방수 수리부터 정산까지 원스톱으로 대행해 드립니다.
              </p>
            </div>
          </ScrollReveal>

          {/* 4 Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, idx) => (
              <ScrollReveal key={idx} direction="up" delay={idx * 0.1}>
                <div className="h-full bg-slate-900/40 backdrop-blur-md rounded-2xl p-6 border border-slate-800/80 hover:border-sky-500/40 transition-all group flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-mono font-black text-sky-400 bg-sky-500/10 border border-sky-500/20 px-2.5 py-1 rounded-md inline-block mb-4">
                      STEP {s.step}
                    </div>
                    
                    <h4 className="text-base font-bold text-white mb-2 tracking-tight group-hover:text-sky-300 transition-colors">
                      {s.title}
                    </h4>

                    <p className="text-xs text-slate-400 leading-relaxed font-normal keep-all">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>

        {/* Bottom Callout CTA */}
        <ScrollReveal direction="up" className="mt-16">
          <div className="text-center">
            <button
              onClick={scrollToConsultation}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-black text-sm sm:text-base transition-all shadow-lg shadow-sky-500/20 active:scale-95 cursor-pointer group"
            >
              <span>무료 부지 현장 조사 & 수익 분석 신청하기</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
};
